import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { selectSolveAnnouncement } from "store/functions/solveAnnouncement";
import announce from "functions/a11y/announce.js";

/**
 * Announces solve outcomes to screen readers.
 *
 * The map paints new routes silently; the completion dialog covers some paths,
 * but assistive-tech users otherwise get no summary of what changed. The
 * memoized selector builds that summary ("Optimization complete: 3 routes, 41
 * stops served…") and this pushes it into the app's single shared live region
 * (functions/a11y/announce.js), which also carries the table row/paste/clear
 * announcements.
 *
 * Replaces the earlier LiveAnnouncer, which rendered its own second live
 * region. Two regions is a real hazard, not just duplication: with both mounted
 * the reading order of concurrent messages is undefined, and a caller has to
 * know which region their message belongs to. One imperative region also works
 * from plain event handlers, which a render-coupled region cannot.
 *
 * Mounted once per layout as a leaf, so its store subscription re-renders only
 * this component (the same isolation reasoning as SolvePoller).
 *
 * Renders nothing — the region itself lives in the shared module.
 */
export default function SolveAnnouncer() {
  const announcement = useSelector(selectSolveAnnouncement);

  // The previous component rendered text into a region, and an aria-live region
  // does NOT announce content already present when it mounts. announce() is
  // imperative, so a naive effect would speak a *stale* result on every page
  // load that happens to have results in state — including a reload, or simply
  // navigating back to the model page. Seed the ref on first run so we only
  // speak genuine changes.
  const lastAnnounced = useRef(null);
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      lastAnnounced.current = announcement;
      return;
    }
    if (!announcement || announcement === lastAnnounced.current) return;
    lastAnnounced.current = announcement;
    announce(announcement);
  }, [announcement]);

  return null;
}
